import React from 'react'
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  Link,
  ProgressBar,
  Text,
  Badge,
} from '@shopify/polaris'
import { useTranslation } from 'react-i18next'
import { numberFormater } from '../../../utils/formater'
import {
  calculateRevenueAttribution,
  formatBillingPeriod,
  formatDate,
  getRemainingTrialDays,
} from './accountHelpers'

/**
 * Account card component for REVENUE_ATTRIBUTION pricing strategy
 * Handles both trial and active subscription states with revenue-based pricing
 */
const RevenueAccountCard = ({
  config,
  billingCycle,
  shopPlan,
  dashboardData,
  pricing,
  onOpenBilling,
}) => {
  const { t } = useTranslation()
  const isTrialPeriod = billingCycle?.isTrialPeriod || false
  
  // Calculate revenue attribution data
  const revenueData = calculateRevenueAttribution(billingCycle, shopPlan)
  const remainingTrialDays = getRemainingTrialDays(billingCycle)

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  // ============================================
  // Trial Period Component
  // ============================================
  if (isTrialPeriod) {
    return (
      <Card padding={'0'}>
        {/* Trial Header */}
        <Box padding="400">
          <BlockStack gap="300">
            <Banner tone="info">
              <BlockStack gap="200">
                <Text variant="headingMd" as="h3">
                  {t('SettingsPage.Sections.Account.Card.Revenue.trialTitle', { planName: config.planName })}
                </Text>
                <Text dangerouslySetInnerHTML={{ __html: t('SettingsPage.Sections.Account.Card.Revenue.trialEndsOn', { date: formatDate(billingCycle.endingOn) }) }} />
                {remainingTrialDays > 0 && (
                  <Text variant="bodySm">
                    {t('SettingsPage.Sections.Account.Card.Revenue.daysRemaining', { count: remainingTrialDays })}
                  </Text>
                )}
                <Text variant="bodySm" tone="subdued">
                  {t('SettingsPage.Sections.Account.Card.Revenue.trialNote')}
                </Text>
              </BlockStack>
            </Banner>

            {/* Trial Information */}
            <BlockStack gap="200">
              <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.Revenue.title')}</Text>

              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="200">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {t('SettingsPage.Sections.Account.Card.Revenue.howItWorks')}
                  </Text>

                  <BlockStack gap="100">
                    <InlineStack gap="100">
                      <Badge>1</Badge>
                      <Text variant="bodySm">
                        {t('SettingsPage.Sections.Account.Card.Revenue.steps.step1Title')}
                      </Text>
                    </InlineStack>

                    <InlineStack gap="100">
                      <Badge>2</Badge>
                      <Text variant="bodySm">
                        {t('SettingsPage.Sections.Account.Card.Revenue.steps.step2Title', { percent: revenueData.revenuePercent || 2 })}
                      </Text>
                    </InlineStack>

                    <InlineStack gap="100">
                      <Badge>3</Badge>
                      <Text variant="bodySm">
                        {t('SettingsPage.Sections.Account.Card.Revenue.steps.step3Title')}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </Box>

              <Box padding="300" background="bg-surface" borderRadius="200">
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">
                    {t('SettingsPage.Sections.Account.Card.Revenue.revenueShare')}
                  </Text>
                  <InlineStack align="space-between">
                    <Text variant="bodyMd" fontWeight="semibold">
                      {t('SettingsPage.Sections.Account.Card.Revenue.percentageLabel')}
                    </Text>
                    <Badge tone="info">
                      {revenueData.revenuePercent || 2}%
                    </Badge>
                  </InlineStack>
                </BlockStack>
              </Box>

              <Text variant="bodySm" tone="subdued">
                {t('SettingsPage.Sections.Account.Card.Revenue.trialRevenueNote')}
              </Text>
            </BlockStack>

            {/* Upgrade CTA during trial */}
            <InlineStack align="end">
              <Button
                onClick={() => pricing.open({ abovePlans: dashboardData.shopPlan })}
              >
                {t('SettingsPage.Sections.Account.Card.viewPlans')}
              </Button>
            </InlineStack>
          </BlockStack>
        </Box>
      </Card>
    )
  }

  // ============================================
  // Active Subscription Component
  // ============================================
  return (
    <Card padding={'0'}>
      {/* Plan Header */}
      <Box padding="400">
        <BlockStack gap="300">
          <InlineGrid columns="1fr auto" gap="300" alignItems="center">
            <BlockStack gap="100">
              <Text variant="headingMd" as="h3">
                {t('SettingsPage.Sections.Account.Card.planLabel', { planName: config.planName })}
              </Text>
              <Text tone="subdued">
                {t('SettingsPage.Sections.Account.Card.Revenue.pricingType', { percent: revenueData.revenuePercent })}
              </Text>
            </BlockStack>
            
            {config.upgradeRequired ? (
              <Button
                url="/pricing"
                primary
              >
                {t('SettingsPage.Sections.Account.upgradeBtnCta')}
              </Button>
            ) : (
              <Link monochrome url="/pricing">
                {t('DefaultText.downgradeText')}
              </Link>
            )}
          </InlineGrid>
        </BlockStack>
      </Box>

      <Divider />

      {/* Revenue Attribution Stats */}
      <Box padding="400">
        <BlockStack gap="300">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.Revenue.attributionTitle')}</Text>

          <InlineGrid columns="1fr 1fr 1fr" gap="300">
            {/* Attributed Revenue */}
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.attributedRevenue')}</Text>
              <Text variant="bodyLg" fontWeight="semibold">
                {formatCurrency(revenueData.coveredRevenue)}
              </Text>
            </BlockStack>

            {/* Revenue Share */}
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.revenueShare')}</Text>
              <Box>
                <Badge tone="info">
                  {revenueData.revenuePercent}%
                </Badge>
              </Box>
            </BlockStack>

            {/* Current Charge */}
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.currentCharge')}</Text>
              <Text variant="bodyLg" fontWeight="semibold">
                {formatCurrency(revenueData.estimatedCharge)}
              </Text>
            </BlockStack>
          </InlineGrid>

          {/* Performance Metrics */}
          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="200">
              <Text variant="bodyMd" fontWeight="semibold">
                {t('SettingsPage.Sections.Account.Card.Revenue.metrics.performanceMetrics')}
              </Text>

              <InlineGrid columns="1fr 1fr" gap="300">
                <BlockStack gap="100">
                  <InlineStack align="space-between">
                    <Text variant="bodySm">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.conversionRate')}</Text>
                    <Text variant="bodySm" fontWeight="semibold">
                      {((revenueData.coveredRevenue / 10000) * 100).toFixed(2)}%
                    </Text>
                  </InlineStack>

                  <InlineStack align="space-between">
                    <Text variant="bodySm">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.avgOrderValue')}</Text>
                    <Text variant="bodySm" fontWeight="semibold">
                      {formatCurrency(revenueData.coveredRevenue / Math.max(1, billingCycle?.currentOrderCount || 1))}
                    </Text>
                  </InlineStack>
                </BlockStack>

                <BlockStack gap="100">
                  <InlineStack align="space-between">
                    <Text variant="bodySm">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.ordersAttributed')}</Text>
                    <Text variant="bodySm" fontWeight="semibold">
                      {numberFormater(billingCycle?.currentOrderCount || 0)}
                    </Text>
                  </InlineStack>

                  <InlineStack align="space-between">
                    <Text variant="bodySm">{t('SettingsPage.Sections.Account.Card.Revenue.metrics.roi')}</Text>
                    <Badge tone="success">
                      {((revenueData.coveredRevenue / Math.max(1, parseFloat(revenueData.estimatedCharge))) * 100).toFixed(0)}x
                    </Badge>
                  </InlineStack>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Box>
        </BlockStack>
      </Box>

      <Divider />

      {/* Billing Period Details */}
      <Box padding="400">
        <BlockStack gap="300">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.Revenue.period.currentPeriod')}</Text>

          <InlineGrid columns="1fr 1fr" gap="300">
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.currentPeriod')}</Text>
              <Text fontWeight="semibold">
                {formatBillingPeriod(billingCycle)}
              </Text>
            </BlockStack>

            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.Revenue.period.daysRemaining')}</Text>
              <Text fontWeight="semibold">
                {getRemainingTrialDays({ ...billingCycle, isTrialPeriod: false })} {t('SettingsPage.Sections.Account.Card.Revenue.period.days')}
              </Text>
            </BlockStack>
          </InlineGrid>

          {/* Revenue Progress */}
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text>{t('SettingsPage.Sections.Account.Card.Revenue.period.revenueGenerated')}</Text>
              <Text fontWeight="semibold">
                {formatCurrency(revenueData.coveredRevenue)}
              </Text>
            </InlineStack>

            <ProgressBar
              progress={Math.min(100, (revenueData.coveredRevenue / 10000) * 100)}
              tone="success"
              size="small"
            />

            <Text variant="bodySm" tone="subdued">
              {t('SettingsPage.Sections.Account.Card.Revenue.period.trackingNote')}
            </Text>
          </BlockStack>
        </BlockStack>
      </Box>

      <Divider />

      {/* How It Works */}
      <Box padding="400">
        <BlockStack gap="300">
          <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.Revenue.howItWorksTitle')}</Text>

          <BlockStack gap="200">
            <Box padding="200" background="bg-surface" borderRadius="100">
              <InlineStack gap="200">
                <Badge>1</Badge>
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.customerInteraction')}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.customerInteractionDesc')}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>

            <Box padding="200" background="bg-surface" borderRadius="100">
              <InlineStack gap="200">
                <Badge>2</Badge>
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.purchaseTracking')}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.purchaseTrackingDesc')}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>

            <Box padding="200" background="bg-surface" borderRadius="100">
              <InlineStack gap="200">
                <Badge>3</Badge>
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.revenueAttribution')}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.revenueAttributionDesc')}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>

            <Box padding="200" background="bg-surface" borderRadius="100">
              <InlineStack gap="200">
                <Badge>4</Badge>
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.billingCalculation')}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {t('SettingsPage.Sections.Account.Card.Revenue.steps.billingCalculationDesc', { percent: revenueData.revenuePercent })}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>
          </BlockStack>

          <Banner tone="info">
            <Text variant="bodySm">
              {t('SettingsPage.Sections.Account.Card.Revenue.valueBanner')}
            </Text>
          </Banner>
        </BlockStack>
      </Box>

      {/* Billing Management */}
      <Divider />
      <Box padding="400">
        <InlineGrid columns="1fr auto" gap="300" alignItems="center">
          <Text>
            {t('SettingsPage.Sections.Account.Card.Billing.description')}
          </Text>
          <Button onClick={onOpenBilling}>
            {t('SettingsPage.Sections.Account.Card.Billing.btnCta')}
          </Button>
        </InlineGrid>
      </Box>
    </Card>
  )
}

export default RevenueAccountCard